import ActivityLogServ from '@api/ActivityLogs/ActivityLogServ';
import { describeChanges, FieldSpecs, money, percent, quoted } from '@api/ActivityLogs/DescribeChanges';
import { ActivityOrigin, buildActivityOrigin } from '@api/ActivityLogs/ActivityOrigin';
import { Dish } from '@api/Dishes/Dish';
import { Recipe } from '@api/Recipes/Recipe';
import { ModifierGroup } from '@api/ModifierGroups/ModifierGroup';
import { Input } from '@api/Inputs/Input';
import { ProductCategory } from '@api/ProductCategories/ProductCategory';
import { InputCategory } from '@api/InputCategories/InputCategory';

/**
 * Qué se audita del CATÁLOGO (M2 del `PLAN-BITACORA-ACTIVITY-LOGS.md`).
 *
 * Es el hueco que abrió este plan: hasta el 2026-09-10 el catálogo **solo
 * registraba borrados**. Se podía cambiar el PRECIO de un producto, el costo de
 * un insumo o el orden del menú y no quedaba rastro de quién ni de cuándo — la
 * pregunta número uno de cualquier auditoría de catálogo.
 *
 * ⚠️ **Costo**: una escritura por guardado que CAMBIE algo, más una lectura del
 * documento anterior para saber qué cambió. Guardar catálogo es cosa de unas
 * pocas veces al día (y ya cuesta 336 lecturas por republicar el snapshot), así
 * que es despreciable. **Si no cambió nada, no se escribe.**
 *
 * ⚠️ **Crear NO se registra**: desde D9 el documento nace con `createdAt` y
 * `createdBy` encima, y el drawer lo deriva de ahí (D8). Escribir además un
 * renglón "creó el producto" sería duplicar el mismo dato (D6) y costar una
 * escritura por cada alta.
 */

export const DISH_FIELDS: FieldSpecs<Dish> = {
    name: { label: 'el nombre', format: quoted },
    // La pregunta #1 de una auditoría de catálogo.
    cost: { label: 'el precio', format: money },
    status: { label: 'el estado', verbs: { on: 'activó el producto', off: 'desactivó el producto' } },
    availableOnline: {
        label: 'la venta en línea',
        verbs: { on: 'lo puso a la venta en línea', off: 'lo quitó de la venta en línea' }
    },
    priority: { label: 'la prioridad en el menú' },
    description: { label: 'la descripción', format: quoted },
    photoSrc: { label: 'la foto', mention: 'cambió la foto del producto' },
    productCategoryId: { label: 'la categoría', mention: 'cambió la categoría del producto' },
    branchOfficesAvailable: { label: 'las sucursales', mention: 'cambió en qué sucursales se vende' },
    components: { label: 'los componentes', mention: 'cambió las recetas que lleva el producto' },
    modifierGroups: { label: 'los modificadores', mention: 'cambió los modificadores del producto' }
};

export const RECIPE_FIELDS: FieldSpecs<Recipe> = {
    name: { label: 'el nombre', format: quoted },
    // Derivado que `saveRecipe` recalcula SIEMPRE: si cambia sin que se toquen
    // los items, es que cambió el costo de un insumo. Eso también vale saberlo.
    cost: { label: 'el costo de la receta', format: money },
    status: { label: 'el estado', verbs: { on: 'activó la receta', off: 'desactivó la receta' } },
    availableForModifiers: {
        label: 'el uso en modificadores',
        verbs: { on: 'la habilitó para modificadores', off: 'la deshabilitó para modificadores' }
    },
    items: { label: 'los insumos', mention: 'cambió los insumos de la receta' }
};

export const MODIFIER_GROUP_FIELDS: FieldSpecs<ModifierGroup> = {
    name: { label: 'el nombre', format: quoted },
    options: { label: 'las opciones', mention: 'cambió las opciones del modificador' }
};

export const INPUT_FIELDS: FieldSpecs<Input> = {
    name: { label: 'el nombre', format: quoted },
    // Los dos costos que alimentan el margen de todo el catálogo.
    purchaseCost: { label: 'el costo de compra', format: money },
    costOfSale: { label: 'el costo de traspaso a sucursal', format: money },
    unitCost: { label: 'el costo por unidad base', format: money },
    unitCostSale: { label: 'el costo de traspaso por unidad base', format: money },
    merma: { label: 'la merma', format: percent },
    presentation: { label: 'la presentación', mention: 'cambió la presentación del insumo' },
    unit: { label: 'la unidad base', mention: 'cambió la unidad base del insumo' },
    inputCategoryId: { label: 'la categoría', mention: 'cambió la categoría del insumo' },
    sufficientThreshold: { label: 'el umbral de stock suficiente' },
    lowThreshold: { label: 'el umbral de stock bajo' },
    criticalThreshold: { label: 'el umbral de stock crítico' },
    distributionCentersAvailable: { label: 'los CEDIS', mention: 'cambió en qué CEDIS está disponible' }
};

export const PRODUCT_CATEGORY_FIELDS: FieldSpecs<ProductCategory> = {
    name: { label: 'el nombre', format: quoted },
    priority: { label: 'la posición en el menú' }
};

export const INPUT_CATEGORY_FIELDS: FieldSpecs<InputCategory> = {
    name: { label: 'el nombre', format: quoted }
};

/**
 * Registra la EDICIÓN de un objeto del catálogo, si de verdad cambió algo.
 *
 * Nunca lanza (`addActivityLog` ya se traga sus errores) y **no registra las
 * altas**: ésas las cubre el sello `createdAt`/`createdBy` del propio documento.
 */
export const logCatalogChange = async <T extends object>(
    section: string,
    sectionId: string | undefined,
    before: T | null | undefined,
    after: T,
    fields: FieldSpecs<T>
): Promise<void> => {
    if (!sectionId || !before) return;
    const description = describeChanges(before, after, fields);
    if (!description) return;
    await ActivityLogServ.addActivityLog(section, sectionId, description);
};

/**
 * El renglón de creación derivado de un objeto del catálogo (D8), listo para el
 * drawer. Vacío si el objeto no trae sello — o sea, **todo lo que ya existía
 * antes del 2026-09-10**: esos documentos nunca sabrán cuándo nacieron, y eso es
 * preferible a inventarles una fecha.
 */
export const buildCatalogOrigin = (item: unknown, verb: string, noun: string): ActivityOrigin[] => {
    const origin = buildActivityOrigin(item as Record<string, unknown>, { verb, noun });
    return origin ? [origin] : [];
};
